import { isVectorSource, type SourceDescriptor } from '../data/model';
import {
  annotationLayerLegendEligibility,
  annotationLegendSymbol,
  effectiveAnnotationLegendLabel,
  type AnnotationLegendSymbol,
} from '../annotations/legend';
import type { AnnotationLayer } from '../annotations/model';
import type { LayerDefinition } from '../project/model';
import type { VectorSymbols } from '../styles/model';
import { symbolGeometryForSource, type GeometryFamily } from '../styles/geometry';
import { deriveLegendEntries } from '../styles/legend';

export interface CompositionLegendLayer {
  readonly layerId: string;
  readonly name: string;
  readonly headingEnabled: boolean;
  readonly geometry: GeometryFamily | 'mixed' | 'annotation';
  readonly entries: readonly CompositionLegendEntry[];
}

export interface CompositionLegendEntry {
  readonly id: string;
  readonly label: string;
  readonly colour: string;
  readonly symbols?: VectorSymbols;
  readonly annotationSymbol?: AnnotationLegendSymbol;
}

export function deriveCompositionLegend(
  sources: readonly SourceDescriptor[],
  layers: readonly LayerDefinition[],
  excludedLayerIds: readonly string[] = [],
  excludedEntryKeysByLayer: Readonly<Record<string, readonly string[]>> = {},
  annotationLayers: readonly AnnotationLayer[] = [],
  hiddenGroupHeadingIds: readonly string[] = [],
): readonly CompositionLegendLayer[] {
  const sourcesById = new Map(sources.map((source) => [source.id, source]));
  const excluded = new Set(excludedLayerIds);
  const hiddenHeadings = new Set(hiddenGroupHeadingIds);
  const dataLegend = layers.flatMap((layer) => {
    if (!layer.visible || excluded.has(layer.id)) return [];
    const source = sourcesById.get(layer.sourceId);
    if (!source || !isVectorSource(source)) return [];
    const excludedEntries = new Set(excludedEntryKeysByLayer[layer.id] ?? []);
    const entries = deriveLegendEntries(layer.name, layer.style).filter(
      (entry) => !excludedEntries.has(entry.id),
    );
    return entries.length
      ? [
          {
            layerId: layer.id,
            name: layer.name,
            headingEnabled: !hiddenHeadings.has(layer.id),
            geometry: symbolGeometryForSource(source),
            entries,
          },
        ]
      : [];
  });
  const annotationLegend = annotationLayers.flatMap((layer): readonly CompositionLegendLayer[] => {
    if (!layer.visible || excluded.has(layer.id)) return [];
    const excludedEntries = new Set(excludedEntryKeysByLayer[layer.id] ?? []);
    const eligibility = annotationLayerLegendEligibility(layer);
    if (layer.includeInLegend && eligibility.eligible) {
      const entryId = `annotation-layer:${layer.id}`;
      if (excludedEntries.has(entryId)) return [];
      return [
        {
          layerId: layer.id,
          name: layer.name,
          headingEnabled: !hiddenHeadings.has(layer.id),
          geometry: 'annotation',
          entries: [
            {
              id: entryId,
              label: effectiveAnnotationLegendLabel(layer),
              colour: eligibility.symbol.kind === 'line' ? eligibility.symbol.colour : '#000000',
              annotationSymbol: eligibility.symbol,
            },
          ],
        },
      ];
    }
    const entries: CompositionLegendEntry[] = layer.annotations.flatMap((annotation) => {
      if (!annotation.visible || !annotation.includeInLegend) return [];
      const symbol = annotationLegendSymbol(annotation);
      if (!symbol) return [];
      const entryId = `annotation:${annotation.id}`;
      if (excludedEntries.has(entryId)) return [];
      return [
        {
          id: entryId,
          label: effectiveAnnotationLegendLabel(annotation),
          colour: symbol.kind === 'line' ? symbol.colour : '#000000',
          annotationSymbol: symbol,
        },
      ];
    });
    return entries.length
      ? [
          {
            layerId: layer.id,
            name: layer.name,
            headingEnabled: !hiddenHeadings.has(layer.id),
            geometry: 'annotation',
            entries,
          },
        ]
      : [];
  });
  return [...dataLegend, ...annotationLegend];
}
